import type React from "react";

export type StoreV2IconName =
  | "phone"
  | "message"
  | "viber"
  | "telegram"
  | "mail"
  | "map"
  | "instagram"
  | "copy"
  | "gift"
  | "box"
  | "users"
  | "cart"
  | "camera"
  | "refresh"
  | "bank"
  | "check"
  | "help";

const paths: Record<StoreV2IconName, React.ReactNode> = {
  phone: <path d="M7 3H4a1 1 0 0 0-1 1c0 9.4 7.6 17 17 17a1 1 0 0 0 1-1v-3l-4-1-1 2c-4.5-1.5-8-5-9.5-9.5l2-1L7 3Z" />,
  message: <path d="M4 5h16v11H9l-5 4V5Zm4 5h8M8 13h5" />,
  // Local Viber-like chat bubble (no third-party assets).
  viber: (
    <>
      <path d="M12 3c-4.4 0-8 3.1-8 7 0 2.2 1.1 4.2 2.9 5.5L6 20l3.2-1.7c.9.3 1.8.4 2.8.4 4.4 0 8-3.1 8-7S16.4 3 12 3Z" />
      <path d="M9 9.5h6M9 12.5h4" />
    </>
  ),
  telegram: <path d="m3 11 17-7-4 16-5-6-4 3 1-5 8-5-10 4-3 0Z" />,
  mail: <path d="M3 5h18v14H3V5Zm1 1 8 7 8-7" />,
  map: <path d="M12 21s7-6 7-12a7 7 0 1 0-14 0c0 6 7 12 7 12Zm0-9a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />,
  instagram: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <path d="M17.5 6.5h.01" />
    </>
  ),
  copy: <path d="M8 8h12v12H8V8Zm-4 8V4h12" />,
  gift: (
    <path d="M3 9h18v12H3V9Zm9 0v12M2 5h20v4H2V5Zm10 0c-4 0-5-4-2-4 2 0 2 4 2 4Zm0 0c4 0 5-4 2-4-2 0-2 4-2 4Z" />
  ),
  box: <path d="m3 7 9-4 9 4-9 4-9-4Zm0 0v10l9 4 9-4V7M12 11v10" />,
  users: <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm8-1a4 4 0 0 1 0 8" />,
  // MERCH-129A-R1 — local cart glyph for product-card cart navigation (not emoji).
  cart: (
    <>
      <path d="M3 5h2l1.5 10h11L20 7H7" />
      <circle cx="9" cy="19" r="1.4" />
      <circle cx="17" cy="19" r="1.4" />
    </>
  ),
  // R7.2.2F4 — deterministic camera SVG (never emoji / font glyph — Telegram Android mojibake).
  camera: (
    <>
      <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z" />
      <circle cx="12" cy="14" r="3.5" />
    </>
  ),
  refresh: (
    <>
      <path d="M21 12a9 9 0 0 0-15.5-6.4L3 8" />
      <path d="M3 3v5h5" />
      <path d="M3 12a9 9 0 0 0 15.5 6.4L21 16" />
      <path d="M21 21v-5h-5" />
    </>
  ),
  bank: (
    <>
      <path d="M3 10h18" />
      <path d="M12 3 3 10h18L12 3Z" />
      <path d="M5 10v8M9 10v8M15 10v8M19 10v8" />
      <path d="M3 18h18" />
    </>
  ),
  check: <path d="M5 12.5 9.5 17 19 7" />,
  help: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.6 2.2c-.8.4-1.1.8-1.1 1.8" />
      <path d="M12 17h.01" />
    </>
  ),
};

export function StoreV2Icon({ name, size = 20 }: { name: StoreV2IconName; size?: number }) {
  return (
    <svg
      className="sv2-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}
